import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Megaphone, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useAuth } from "../context/AuthContext";
import { api, fileUrl, fmtDate, formatErr } from "../lib/api";
import { Empty, Field, PageHeader, UploadButton, inputCls } from "./kit";

const AUD = [["everyone", "Everyone"], ["customers", "Customers Only"], ["grinders", "Grinders Only"], ["both", "Customers and Grinders"]];
const TYPES = ["announcement", "promotion", "update", "maintenance"];

function Editor({ item, onSaved }) {
  const [f, setF] = useState(item || { title: "", description: "", image: "", type: "announcement", audience: "everyone", published: true, important: false });
  const save = async () => {
    try {
      if (f.id) await api.put(`/admin/announcements/${f.id}`, f);
      else await api.post("/admin/announcements", f);
      toast.success("Announcement saved");
      onSaved();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };
  return (
    <div className="grid gap-3" data-testid="announcement-editor">
      <Field label="Title"><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={inputCls} data-testid="ann-title-input" /></Field>
      <Field label="Description"><Textarea rows={4} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className={inputCls} data-testid="ann-desc-input" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Type"><Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}><SelectTrigger className={inputCls} data-testid="ann-type-select"><SelectValue /></SelectTrigger><SelectContent className="bg-[#060d20]">{TYPES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent></Select></Field>
        <Field label="Target audience"><Select value={f.audience} onValueChange={(v) => setF({ ...f, audience: v })}><SelectTrigger className={inputCls} data-testid="ann-audience-select"><SelectValue /></SelectTrigger><SelectContent className="bg-[#060d20]">{AUD.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></Field>
      </div>
      <div className="flex items-center gap-3">{f.image && <img src={fileUrl(f.image)} alt="" className="w-24 h-16 object-cover rounded" />}<UploadButton scope="public" onUploaded={(r) => setF({ ...f, image: r.url })} testid="ann-image-upload">Upload image</UploadButton>{f.image && <button className="text-xs text-rose-300" onClick={() => setF({ ...f, image: "" })}>Remove</button>}</div>
      <label className="flex items-center gap-3 text-sm"><Switch checked={f.published} onCheckedChange={(v) => setF({ ...f, published: v })} data-testid="ann-published-switch" /> Published</label>
      <label className="flex items-center gap-3 text-sm"><Switch checked={f.important} onCheckedChange={(v) => setF({ ...f, important: v })} data-testid="ann-important-switch" /> Important (show as dashboard banner)</label>
      <div className="flex gap-2"><button onClick={save} className="btn-electric !py-2" data-testid="ann-save-btn">Save</button><button onClick={() => onSaved(true)} className="btn-ghost !py-2">Cancel</button></div>
    </div>
  );
}

export default function Announcements() {
  const { user } = useAuth();
  const admin = user.role === "admin";
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/announcements").then((r) => setItems(r.data)), []);
  useEffect(() => {
    load();
  }, [load]);
  const del = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    await api.delete(`/admin/announcements/${id}`);
    load();
  };
  return (
    <div data-testid="announcements-page">
      <PageHeader title={user.role === "grinder" ? "Announcements" : "Announcements & Promos"} desc={admin ? "Control exactly who sees each announcement" : "Latest news and promotions from RushGrind2K"}>
        {admin && <button onClick={() => setEdit({})} className="btn-electric !py-2" data-testid="ann-new-btn"><Plus className="w-4 h-4" /> New announcement</button>}
      </PageHeader>
      {items.length === 0 ? <Empty text="No announcements yet." /> : (
        <div className="grid gap-4 md:grid-cols-2">
          {items.map((a) => (
            <article key={a.id} className="card-neon overflow-hidden" data-testid={`announcement-${a.id}`}>
              {a.image && <img src={fileUrl(a.image)} alt="" className="w-full h-44 object-cover" />}
              <div className="p-5">
                <div className="flex flex-wrap items-center gap-2 text-xs"><span className="px-2 py-0.5 rounded-full bg-[#007bff]/20 text-[#5cc8ff] uppercase tracking-wider flex items-center gap-1"><Megaphone className="w-3 h-3" />{a.type}</span>
                  {admin && <span className="text-slate-400">{AUD.find((x) => x[0] === a.audience)?.[1]} · {a.published ? "Published" : "Draft"}{a.important ? " · Banner" : ""}</span>}</div>
                <h3 className="font-cond font-bold text-2xl uppercase mt-2 text-white">{a.title}</h3>
                <p className="text-slate-300 whitespace-pre-wrap mt-1">{a.description}</p>
                <p className="text-xs text-slate-500 mt-3">{fmtDate(a.created_at)}</p>
                {admin && <div className="flex gap-2 mt-3"><button onClick={() => setEdit(a)} className="btn-ghost !py-1.5 !px-3 text-sm" data-testid={`ann-edit-${a.id}`}>Edit</button><button onClick={() => del(a.id)} className="btn-ghost !py-1.5 !px-3 text-sm !text-rose-300 !border-rose-500/40" data-testid={`ann-delete-${a.id}`}>Delete</button></div>}
              </div>
            </article>
          ))}
        </div>
      )}
      <Dialog open={!!edit} onOpenChange={() => setEdit(null)}>
        <DialogContent className="bg-[#050a18] border-[#007bff]/40 max-h-[90vh] overflow-y-auto"><DialogTitle className="font-cond uppercase">{edit?.id ? "Edit" : "New"} announcement</DialogTitle>
          {edit && <Editor item={edit.id ? edit : null} onSaved={() => { setEdit(null); load(); }} />}</DialogContent>
      </Dialog>
    </div>
  );
}
